import { ItemStatuses } from 'constants/statuses';
import { DataForCards } from 'flows/core/hooks/use-data-for-cards';
import { BadgeConfig, CardDataWithHeroExtras } from 'flows/core/types/cards';
import { EssentialCards } from 'flows/core/types/layouts';
import { CLOUDINARY_BASE_URL } from 'flows/insurance/constants';
import { getProviderData } from 'flows/insurance/constants/providers';
import merge from 'lodash/merge';
import { ItemKinds } from 'types';
import { Item } from 'types/item';
import { Badges, enabledItemDefOfKind, isCompleted, itemOfKind } from './utils';

const KIND = ItemKinds.HOMEOWNER_INSURANCE;

const INSURANCE_COMMON_STATIC_DATA = {
  subtype: 'SYI',
  icon: 'SHIELD',
  title: 'Secure home insurance',
  action: {
    type: 'internal',
    route: '/insurance/homeowner',
  },
  completionTimeText: '< 5 min',
} as const;

const INSURANCE_INITIATE_STATIC_DATA = {
  ...INSURANCE_COMMON_STATIC_DATA,
  description: 'Purchase protection for your new home.',
} as const;

export const statusToBadge = (
  status?: string,
  tab?: string,
  item?: Item
): BadgeConfig | undefined => {
  if (
    status === ItemStatuses.STARTED &&
    tab !== 'today' &&
    item?.metadata?.viewed === true
  ) {
    return Badges.RECENTLY_VIEWED;
  }
  switch (status) {
    case ItemStatuses.UNVERIFIED:
      return Badges.ALMOST_DONE;
    case ItemStatuses.VALIDATING:
      return Badges.IN_REVIEW;
    case ItemStatuses.COMPLETED:
      return Badges.DONE;
    case ItemStatuses.INFORMATION_REQUESTED:
    case ItemStatuses.INFO_REQUESTED:
      return Badges.NEEDS_MORE;
    default:
      return undefined;
  }
};

export const statusToImportance = (status?: string) => {
  if (status === 'started') {
    return 750_001;
  }
  if (status === 'completed') {
    return 0;
  }
  return 1_000_000;
};

export const makeTodayCardJson = (
  status: ItemStatuses,
  commonData: Record<string, unknown>,
  payloadKind: 'INITIATE' | 'PROMOTED',
  item?: Item
): CardDataWithHeroExtras => {
  return {
    type: payloadKind,
    // @ts-expect-error FIXME: TS2322 - commonData shape is verified at call sites
    data: {
      ...commonData,
      badge: statusToBadge(status, 'today', item),
    },
    heroExtras: {
      heading: 'Get insurance coverage to keep your stuff safe. ',
      ctaText: 'Get started',
    },
    identifier: `syi_${payloadKind.toLowerCase()}`,
    importanceScore: statusToImportance(status),
  };
};

export const makeTodayTabHomeownersInsuranceCards = ({
  itemDefinitions,
  items,
  user,
}: DataForCards): CardDataWithHeroExtras[] => {
  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  const insuranceItems = items.filter(itemOfKind(KIND));
  const isOwner = user?.currentMove?.toAddress.ownership === 'OWN';
  if (!isOwner || !itemDef?.page) return [];
  if (itemDef) {
    const { status, route } = {
      status: insuranceItems[0]?.status,
      route: 'insurance/homeowner',
    };

    const recommendedBy = itemDef.entity?.name?.split(' ').shift();
    const preferredProviderText = `Recommended by ${recommendedBy}`;

    const cardJson = makeTodayCardJson(
      status as ItemStatuses,
      {
        ...INSURANCE_COMMON_STATIC_DATA,
        action: {
          ...INSURANCE_COMMON_STATIC_DATA.action,
          route,
        },
      },
      'PROMOTED',
      insuranceItems[0] as unknown as Item
    );

    const providerData = getProviderData(itemDef.page?.name) || {
      ...itemDef.page,
      logo: itemDef.page.logo_id
        ? `${CLOUDINARY_BASE_URL}${encodeURI(itemDef.page.logo_id)}`
        : null,
    };
    const preferredProviderJson = merge(cardJson, {
      data: {
        image: providerData?.logo
          ? { url: providerData.logo, alt: providerData.name }
          : {},
        description: preferredProviderText,
      },
    });
    return [preferredProviderJson];
  }

  return [];
};

const essentialsImportanceScore = 1_000_000;
const essentialsType = 'INITIATE';
const essentialsIdentifier = 'syi_initiate';

export const makeEssentialsTabHomeownersInsuranceCards = ({
  itemDefinitions,
  items,
  user,
}: DataForCards): EssentialCards => {
  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  // we always reference just the first insurance item
  const insuranceItem = items.filter(itemOfKind(KIND))[0] as unknown as Item;
  const isOwner = user?.currentMove?.toAddress.ownership === 'OWN';

  const essentialCardLists: EssentialCards = {
    toDoCards: [],
    completedCards: [],
  };

  if (itemDef && Boolean(itemDef.page) && isOwner) {
    const { status, route } = {
      status: insuranceItem?.status ?? '',
      route: 'insurance/homeowner',
    };

    const insuranceStaticData = {
      ...INSURANCE_INITIATE_STATIC_DATA,
      action: {
        ...INSURANCE_INITIATE_STATIC_DATA.action,
        route,
      },
    };

    const targetList = isCompleted({ status }) ? 'completedCards' : 'toDoCards';
    const badge = statusToBadge(
      status as ItemStatuses,
      undefined,
      insuranceItem
    );
    const computedCtaText = badge?.variant === 'done' ? 'View task' : undefined;

    essentialCardLists[targetList].push({
      type: essentialsType,
      data: {
        ...insuranceStaticData,
        badge,
        status,
        ctaText: computedCtaText,
      },
      identifier: `${essentialsIdentifier}${
        isCompleted({ status }) ? '_completed' : ''
      }${insuranceItem ? `_${insuranceItem.id}` : ''}`,
      importanceScore: essentialsImportanceScore,
    });
  }

  return essentialCardLists;
};
